import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ContextProps } from '../types/ContextProps';
import { SaveButtonStatus, useSaveButtonStatus } from 'src/hooks/useSaveButtonStatus';
import { useToast } from 'src/app/useToast';

type StowageSaveStatusContextType = {
    status: SaveButtonStatus;
    beginSave: (successMessage: string) => string;
    reset: () => void;
};

export const StowageSaveStatusContext = React.createContext<StowageSaveStatusContextType>(undefined!);

/**
 * Shared save status for the stowage pop-up panels.
 *
 * The panels close as soon as their save is dispatched, which would unmount a panel-local
 * useSaveButtonStatus (and its service worker listener) long before BACKGROUND_SAVE_COMPLETED
 * arrives. Keeping a single instance here — above every panel mount point — lets the confirmation
 * still be observed and surfaced as a toast after the panel is gone.
 *
 * Active saves are tracked in a map keyed by saveId so that overlapping or queued saves
 * (e.g. user opens and saves two modals in rapid succession) each reliably deliver their
 * respective success toast.
 */
export const StowageSaveStatusProvider = ({ children }: ContextProps) => {
    const { status, markSaving, reset: resetSaveButton } = useSaveButtonStatus();
    const { toastSuccess } = useToast();
    const pendingSavesRef = useRef<Map<string, string>>(new Map());

    const beginSave = useCallback(
        (successMessage: string): string => {
            const saveId = markSaving();
            pendingSavesRef.current.set(saveId, successMessage);
            return saveId;
        },
        [markSaving]
    );

    const reset = useCallback(() => {
        pendingSavesRef.current.clear();
        resetSaveButton();
    }, [resetSaveButton]);

    useEffect(() => {
        const handleSwMessage = (event: MessageEvent) => {
            const { type, saveId } = (event.data ?? {}) as { type?: string; saveId?: string };
            if (!saveId) return;

            if (type === 'BACKGROUND_SAVE_COMPLETED') {
                const message = pendingSavesRef.current.get(saveId);
                if (message) {
                    toastSuccess(message);
                    pendingSavesRef.current.delete(saveId);
                }
            } else if (type === 'BACKGROUND_SAVE_FAILED') {
                pendingSavesRef.current.delete(saveId);
            }
        };

        const handleSaveAborted = (event: Event) => {
            const detail = (event as CustomEvent<{ saveId: string }>).detail;
            if (detail?.saveId) {
                pendingSavesRef.current.delete(detail.saveId);
            }
        };

        window.addEventListener('save-aborted', handleSaveAborted);
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', handleSwMessage);
        }

        return () => {
            window.removeEventListener('save-aborted', handleSaveAborted);
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', handleSwMessage);
            }
        };
    }, [toastSuccess]);

    const value = useMemo(() => ({ status, beginSave, reset }), [status, beginSave, reset]);

    return <StowageSaveStatusContext.Provider value={value}>{children}</StowageSaveStatusContext.Provider>;
};

export const useStowageSaveStatusStore = () => useContext(StowageSaveStatusContext);

